import { create } from 'zustand'

import { safeStorage } from '../storage.ts'

/**
 * 테마 상태. 상태만 들고 있고 색값은 모른다 — 색은 @ctcells/ui 의 토큰 CSS 가 갖는다.
 *
 * 규약:
 *  - 기본은 **라이트**다(eric 지시 2026-09-21 「디폴트로 light 모드로 해줘」). 고른 적이 없으면
 *    OS 가 다크여도 라이트로 선다. 🔴 기본을 'system' 으로 되돌리지 마라.
 *  - 사용자가 고르면 그 선택이 저장소에 남는다. 'system'(시스템 설정 따르기)도 **고른 값**이라
 *    저장한다 — 지우면 다음에 열 때 기본(라이트)으로 돌아가 버린다.
 *  - 테마 판정은 여기 한 곳이다. CSS 에 @media (prefers-color-scheme) 를 두지 않는다.
 *    두 군데서 판정하면 사용자 덮어쓰기와 우선순위가 꼬인다.
 */

/** 사용자가 고를 수 있는 값 */
export type ThemePreference = 'system' | 'light' | 'dark'

/**
 * 고를 수 있는 테마 목록 — **유일한 출처다.** 설정 화면(내 계정)과 모듈 패드 헤더의
 * `ThemeToggle` 이 같은 배열을 그린다. 두 곳에 목록을 복사하지 않는다.
 * 화면 표기는 `theme.<값>` 문구 키로 간다.
 */
export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark']

/** 실제로 화면에 적용되는 값 */
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'ctcells:theme'
/** 고른 적이 없을 때의 값 */
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light'
const DARK_QUERY = '(prefers-color-scheme: dark)'

function isPreference(value: string | null): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark'
}

function readStoredPreference(): ThemePreference {
  const raw = safeStorage.read(THEME_STORAGE_KEY)
  return isPreference(raw) ? raw : DEFAULT_THEME_PREFERENCE
}

/** matchMedia 는 오래된 WebView 에서 없을 수 있다. 없으면 라이트로 본다. */
function systemTheme(): ResolvedTheme {
  try {
    return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? systemTheme() : preference
}

/** 적용 지점도 한 곳. 선택자는 [data-theme] 이다(토큰 CSS 와 짝) */
export function applyTheme(theme: ResolvedTheme): void {
  if (typeof document === 'undefined') return
  document.documentElement.dataset['theme'] = theme
}

interface ThemeState {
  preference: ThemePreference
  theme: ResolvedTheme
  // 화살표 타입으로 선언한다 — 셀렉터로 꺼내 쓸 때 unbound-method 경고를 피한다
  setPreference: (preference: ThemePreference) => void
  /** 시스템 설정이 바뀌었을 때 재계산한다. initTheme 이 붙인 리스너가 부른다 */
  syncSystem: () => void
}

const initialPreference = readStoredPreference()

export const useThemeStore = create<ThemeState>((set, get) => ({
  preference: initialPreference,
  theme: resolveTheme(initialPreference),

  setPreference: (preference) => {
    safeStorage.write(THEME_STORAGE_KEY, preference)

    const theme = resolveTheme(preference)
    set({ preference, theme })
    applyTheme(theme)
  },

  syncSystem: () => {
    if (get().preference !== 'system') return
    const theme = systemTheme()
    if (theme === get().theme) return
    set({ theme })
    applyTheme(theme)
  },
}))

/**
 * 첫 렌더 전에 부른다(mountApp). 저장된 선택을 DOM 에 반영하고
 * 시스템 설정 변경을 구독한다. 반환값은 해제 함수다.
 */
export function initTheme(): () => void {
  applyTheme(useThemeStore.getState().theme)

  let query: MediaQueryList
  try {
    query = window.matchMedia(DARK_QUERY)
  } catch {
    return () => {}
  }

  const onChange = () => useThemeStore.getState().syncSystem()
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}
